/**
 * Screen 13 — the nightly cash-up.
 *
 * The night's shape, in the order somebody standing at the till works
 * through it: read the three tender totals off the POS, count the drawer —
 * note by note if the venue does it that way — read the terminal's
 * settlement, bank the takings, leave tomorrow's float, write down anything
 * odd. The aside adds up as you go.
 *
 * Every figure here is dollars on the screen and integer cents on the wire.
 * The arithmetic is `reconcileCashup` from @baros/core, the same function the
 * API runs on the way out, so the variance shown before the save and the one
 * that comes back after it cannot disagree.
 *
 * Once a manager finalises a night it goes read-only, with who signed it and
 * when. Correcting a finalised cash-up is deliberately not a thing this
 * screen does: the venue banked against that figure.
 */

import { hasFeature } from "../features.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDays,
  AU_DENOMINATIONS,
  CASHUP_WINDOW_DAYS,
  denominationTotal,
  reconcileCashup,
  serviceDate,
  toCents,
  type Cashup,
  type CashupFigures,
  type DenominationCount,
  type VarianceBand, clockOf } from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import { money, venueZone } from "../format.js";
import type { ScreenProps } from "../screens.js";
import { cashupApi, type CashupDraft, type PosFigures } from "./api.js";
import "./cashup.css";

/** The stocktake's three colours, for the stocktake's three bands. */
const BAND_CLASS: Readonly<Record<VarianceBand, string>> = {
  ok: "pos",
  watch: "warn",
  investigate: "neg",
};

const BAND_WORD: Readonly<Record<VarianceBand, string>> = {
  ok: "Within tolerance",
  watch: "Worth a look",
  investigate: "Investigate",
};

/** Dollars typed into a field, as whole cents. Anything unreadable is zero. */
function centsOf(text: string): number {
  const n = Number(text.replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? Math.max(0, toCents(n)) : 0;
}

/** Cents as the plain number a person types back: `912.50`. */
function dollarsOf(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** Cents, signed, for a variance: `+$4.00`, `-$12.50`, `$0.00`. */
function signedMoney(cents: number): string {
  return `${cents > 0 ? "+" : ""}${money(cents / 100)}`;
}

const shortNight = (date: string) =>
  // A calendar date, not an instant: read in UTC so no zone moves it a day.
  new Date(`${date}T12:00:00Z`).toLocaleDateString("en-AU", {
    timeZone: "UTC",
    weekday: "short",
    day: "2-digit",
    month: "short",
  });

/** What the fields hold. Money is text here — a person is typing it. */
interface Form {
  readonly float: string;
  readonly posCash: string;
  readonly posCard: string;
  readonly posOther: string;
  readonly otherNote: string;
  /** Used when the drawer is entered as one figure. */
  readonly counted: string;
  readonly byDenomination: boolean;
  /** Count per denomination, keyed by face value in cents. */
  readonly counts: Readonly<Record<string, string>>;
  readonly terminal: string;
  readonly banked: string;
  readonly retained: string;
  readonly notes: string;
}

const EMPTY: Form = {
  float: "",
  posCash: "",
  posCard: "",
  posOther: "",
  otherNote: "",
  counted: "",
  byDenomination: true,
  counts: {},
  terminal: "",
  banked: "",
  retained: "",
  notes: "",
};

/** A saved record back into the fields. */
function formOf(record: Cashup): Form {
  const counts: Record<string, string> = {};
  for (const row of record.denominations ?? []) {
    counts[String(row.denominationCents)] = row.count ? String(row.count) : "";
  }
  return {
    float: dollarsOf(record.floatCents),
    posCash: dollarsOf(record.posCashCents),
    posCard: dollarsOf(record.posCardCents),
    posOther: record.posOtherCents ? dollarsOf(record.posOtherCents) : "",
    otherNote: record.otherNote,
    counted: dollarsOf(record.countedCashCents),
    byDenomination: record.denominations !== null,
    counts,
    terminal: dollarsOf(record.terminalCardCents),
    banked: dollarsOf(record.bankedCents),
    retained: dollarsOf(record.floatRetainedCents),
    notes: record.notes,
  };
}

/** The breakdown as the API wants it — every denomination, zeroes included. */
function breakdownOf(form: Form): readonly DenominationCount[] {
  return AU_DENOMINATIONS.map((d) => ({
    denominationCents: d.cents,
    count: Math.max(0, Math.round(Number(form.counts[String(d.cents)] ?? 0) || 0)),
  }));
}

function draftOf(form: Form): CashupDraft {
  const breakdown = form.byDenomination ? breakdownOf(form) : null;
  return {
    floatCents: centsOf(form.float),
    posCashCents: centsOf(form.posCash),
    posCardCents: centsOf(form.posCard),
    posOtherCents: centsOf(form.posOther),
    otherNote: form.otherNote,
    countedCashCents: breakdown
      ? denominationTotal(breakdown)
      : centsOf(form.counted),
    denominations: breakdown,
    terminalCardCents: centsOf(form.terminal),
    bankedCents: centsOf(form.banked),
    floatRetainedCents: centsOf(form.retained),
    notes: form.notes,
  };
}

export function CashupScreen(_props: ScreenProps) {
  const app = useApp();
  const { token, venueId, venueState } = app;
  // The venue's clock, so tonight's cash-up is tonight until its cut-off.
  const clock = clockOf(venueState?.venue);
  const canRecord = app.me.can["cashup:record"];
  const canFinalise = app.me.can["cashup:finalise"];

  const [date, setDate] = useState(() => serviceDate(new Date(), { timeZone: clock }));
  const [form, setForm] = useState<Form>(EMPTY);
  const [saved, setSaved] = useState<Cashup | null>(null);
  const [recent, setRecent] = useState<readonly Cashup[]>([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** Square's three figures, kept with the night they are for. */
  const [square, setSquare] = useState<{
    readonly date: string;
    readonly figures: PosFigures;
    /** When the night was last totalled from the till. */
    readonly asOf: string | null;
  } | null>(null);

  const staffNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const person of venueState?.staff ?? []) {
      names.set(person.userId, person.displayName);
    }
    return names;
  }, [venueState]);

  const who = useCallback(
    (userId: string | null) => (userId ? (staffNames.get(userId) ?? userId) : "—"),
    [staffNames],
  );

  /**
   * The night being cashed up, plus the fortnight behind it.
   *
   * A night nobody has touched is a 404, which is the ordinary case at
   * closing time rather than an error. The float is seeded from the last
   * night that was cashed up — whatever was left in the drawer is what is in
   * it now — so the commonest number on the screen is already right.
   */
  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    setNote(null);
    try {
      const list = await cashupApi.list(token, venueId);
      setRecent(list.cashups);

      const existing = list.cashups.find((c) => c.serviceDate === date);
      if (existing) {
        setSaved(existing);
        setForm(formOf(existing));
      } else {
        const last = list.cashups.find((c) => c.serviceDate < date);
        setSaved(null);
        setForm({
          ...EMPTY,
          float: last ? dollarsOf(last.floatRetainedCents) : "",
          retained: last ? dollarsOf(last.floatRetainedCents) : "",
        });
      }
      setDirty(false);
    } catch (err) {
      setError(message(err));
    } finally {
      setLoading(false);
    }
  }, [token, venueId, date]);

  useEffect(() => {
    void load();
  }, [load]);

  const finalised = saved?.status === "finalised";
  const locked = finalised || !canRecord;

  /**
   * What Square rang up, for a night still being counted — the half of this
   * screen somebody otherwise reads off the till and types in.
   *
   * A convenience and nothing more. Where Square is not connected, the venue
   * is not linked, the till has nothing for the night or the call fails for
   * any reason at all, nothing is set and the screen is exactly what it was
   * before there was a till to ask. A finalised night is not asked about:
   * there is nothing left on it to fill in.
   */
  useEffect(() => {
    // Without the Square till (D14) there is nothing to ask.
    if (!venueId || loading || locked || !hasFeature(app.me, "square")) return;
    let live = true;
    cashupApi
      .posDay(token, venueId, date)
      .then((res) => {
        if (!live) return;
        const figures = res?.cashup;
        const readable =
          figures &&
          [figures.posCashCents, figures.posCardCents, figures.posOtherCents].every(
            (n) => typeof n === "number" && Number.isFinite(n),
          );
        setSquare(readable ? { date, figures, asOf: res.day?.updatedAt ?? null } : null);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [token, venueId, date, loading, locked]);

  const set = (patch: Partial<Form>) => {
    setForm((current) => ({ ...current, ...patch }));
    setDirty(true);
    setNote(null);
  };

  const setCount = (cents: number, text: string) => {
    setForm((current) => ({
      ...current,
      counts: { ...current.counts, [String(cents)]: text.replace(/[^0-9]/g, "") },
    }));
    setDirty(true);
    setNote(null);
  };

  const draft = draftOf(form);
  const figures: CashupFigures = draft;
  const reconciliation = reconcileCashup(figures);

  const fromSquare = !locked && !loading && square?.date === date ? square.figures : null;
  const squareEntered =
    fromSquare !== null &&
    draft.posCashCents === fromSquare.posCashCents &&
    draft.posCardCents === fromSquare.posCardCents &&
    draft.posOtherCents === fromSquare.posOtherCents;

  /**
   * Fill the three POS fields. It does not save: the person still does that.
   *
   * The figures are asked for again first. This screen is opened hours before
   * it is finished, and what the till had rung up at eleven is not what it
   * had at close. If the till cannot be asked, what is on screen is used —
   * the line above the fields says what time it is from.
   */
  const fillFromSquare = async () => {
    if (!fromSquare || !venueId) return;
    let figures = fromSquare;
    try {
      const res = await cashupApi.posDay(token, venueId, date);
      if (res?.cashup) {
        figures = res.cashup;
        setSquare({ date, figures, asOf: res.day?.updatedAt ?? null });
      }
    } catch {
      // Keep what was already shown.
    }
    set({
      posCash: dollarsOf(figures.posCashCents),
      posCard: dollarsOf(figures.posCardCents),
      posOther: figures.posOtherCents ? dollarsOf(figures.posOtherCents) : "",
    });
  };

  const squareAsOf =
    square?.asOf && !Number.isNaN(Date.parse(square.asOf))
      ? new Date(square.asOf).toLocaleTimeString("en-AU", { timeZone: venueZone(), hour: "numeric", minute: "2-digit" })
      : null;

  const save = async () => {
    if (!venueId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await cashupApi.save(token, venueId, date, draft);
      setSaved(result.cashup);
      setForm(formOf(result.cashup));
      setDirty(false);
      setNote(`Draft saved for ${date}.`);
      const list = await cashupApi.list(token, venueId);
      setRecent(list.cashups);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const finalise = async () => {
    if (!venueId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await cashupApi.finalise(token, venueId, date);
      setSaved(result.cashup);
      setForm(formOf(result.cashup));
      setDirty(false);
      setNote(`${date} is signed off.`);
      const list = await cashupApi.list(token, venueId);
      setRecent(list.cashups);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  if (!venueId) {
    return (
      <div className="pad">
        <div className="msg err">
          You are not assigned to a venue, so there is no till to cash up.
        </div>
      </div>
    );
  }

  const countedCents = draft.countedCashCents;

  return (
    <div className="screen">
      <div className="split cu-split">
        <div className="pane">
          <div
            className="row screen-head"
            style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14, flexWrap: "wrap" }}
          >
            <div>
              <div className="label">Service {date}</div>
              <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
                {loading
                  ? "Reading the night…"
                  : finalised
                    ? `Signed off by ${who(saved?.finalisedBy ?? null)}`
                    : saved
                      ? "Draft saved. Counting can continue."
                      : "Nothing counted for this night yet."}
              </div>
            </div>
            <span className="spacer" />
            <div className="cu-stepper">
              <button
                className="btn"
                aria-label="The night before"
                onClick={() => setDate(addDays(date, -1))}
              >
                ◂
              </button>
              <label className="field">
                <span>Service date</span>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value || serviceDate(new Date(), { timeZone: clock }))}
                />
              </label>
              <button
                className="btn"
                aria-label="The night after"
                disabled={date >= serviceDate(new Date(), { timeZone: clock })}
                onClick={() => setDate(addDays(date, 1))}
              >
                ▸
              </button>
            </div>
          </div>

          {error && (
            <div style={{ padding: "0 20px 10px" }}>
              <div className="msg err">{error}</div>
            </div>
          )}
          {note && !error && (
            <div style={{ padding: "0 20px 10px" }}>
              <div className="msg ok">{note}</div>
            </div>
          )}
          {!canRecord && (
            <div style={{ padding: "0 20px 10px" }}>
              <div className="msg">Your role does not allow cashing up. This is the record.</div>
            </div>
          )}

          <div className="scroll" style={{ padding: "0 20px 20px" }}>
            {/* ------------------------------------------- what the POS says */}
            <section className="cu-block">
              <div className="label">Takings, per the POS</div>
              {fromSquare && (
                <div className="cu-square">
                  <span>
                    From Square{squareAsOf ? `, as of ${squareAsOf}` : ""}: cash{" "}
                    {money(fromSquare.posCashCents / 100)} · card{" "}
                    {money(fromSquare.posCardCents / 100)} · other{" "}
                    {money(fromSquare.posOtherCents / 100)}
                  </span>
                  {squareEntered ? (
                    <span className="label">Matches what is entered</span>
                  ) : (
                    <button className="btn sm" onClick={() => void fillFromSquare()}>
                      Use these
                    </button>
                  )}
                </div>
              )}
              <div className="cu-fields">
                <label className="field">
                  <span>Opening float</span>
                  <input
                    className="num"
                    inputMode="decimal"
                    disabled={locked}
                    value={form.float}
                    placeholder="0.00"
                    onChange={(e) => set({ float: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Cash</span>
                  <input
                    className="num"
                    inputMode="decimal"
                    disabled={locked}
                    value={form.posCash}
                    placeholder="0.00"
                    onChange={(e) => set({ posCash: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Card</span>
                  <input
                    className="num"
                    inputMode="decimal"
                    disabled={locked}
                    value={form.posCard}
                    placeholder="0.00"
                    onChange={(e) => set({ posCard: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Other tender</span>
                  <input
                    className="num"
                    inputMode="decimal"
                    disabled={locked}
                    value={form.posOther}
                    placeholder="0.00"
                    onChange={(e) => set({ posOther: e.target.value })}
                  />
                </label>
              </div>
              <label className="field cu-wide">
                <span>What the other tender was</span>
                <input
                  type="text"
                  disabled={locked}
                  value={form.otherNote}
                  placeholder="Vouchers, staff accounts…"
                  onChange={(e) => set({ otherNote: e.target.value })}
                />
              </label>
            </section>

            {/* --------------------------------------------- the drawer count */}
            <section className="cu-block">
              <div className="row">
                <div className="label">Counted out of the drawer</div>
                <span className="spacer" />
                <button
                  className="btn sm"
                  aria-pressed={form.byDenomination}
                  disabled={locked}
                  onClick={() => set({ byDenomination: true })}
                >
                  Note by note
                </button>
                <button
                  className="btn sm"
                  aria-pressed={!form.byDenomination}
                  disabled={locked}
                  onClick={() =>
                    set({ byDenomination: false, counted: dollarsOf(countedCents) })
                  }
                >
                  One figure
                </button>
              </div>

              {form.byDenomination ? (
                <div className="cu-denoms">
                  {AU_DENOMINATIONS.map((d) => {
                    const text = form.counts[String(d.cents)] ?? "";
                    const lineCents = (Number(text) || 0) * d.cents;
                    return (
                      <label className="cu-denom" key={d.cents}>
                        <span className="f">{d.label}</span>
                        <input
                          className="num"
                          inputMode="numeric"
                          aria-label={`${d.label} count`}
                          disabled={locked}
                          value={text}
                          placeholder="0"
                          onChange={(e) => setCount(d.cents, e.target.value)}
                        />
                        <span className="t">{lineCents ? money(lineCents / 100) : "—"}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="cu-fields">
                  <label className="field">
                    <span>Cash counted</span>
                    <input
                      className="num"
                      inputMode="decimal"
                      disabled={locked}
                      value={form.counted}
                      placeholder="0.00"
                      onChange={(e) => set({ counted: e.target.value })}
                    />
                  </label>
                </div>
              )}

              <div className="cu-running">
                <span className="a">{money(countedCents / 100)} in the drawer</span>
                <span className={`b ${BAND_CLASS[reconciliation.cash.band]}`}>
                  {signedMoney(reconciliation.cash.deltaCents)} against{" "}
                  {money(reconciliation.expectedCashCents / 100)} expected
                </span>
              </div>
            </section>

            {/* ------------------------------------------- terminal and banking */}
            <section className="cu-block">
              <div className="label">Terminal and banking</div>
              <div className="cu-fields">
                <label className="field">
                  <span>EFTPOS settlement</span>
                  <input
                    className="num"
                    inputMode="decimal"
                    disabled={locked}
                    value={form.terminal}
                    placeholder="0.00"
                    onChange={(e) => set({ terminal: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Banked to the safe</span>
                  <input
                    className="num"
                    inputMode="decimal"
                    disabled={locked}
                    value={form.banked}
                    placeholder="0.00"
                    onChange={(e) => set({ banked: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Float retained</span>
                  <input
                    className="num"
                    inputMode="decimal"
                    disabled={locked}
                    value={form.retained}
                    placeholder="0.00"
                    onChange={(e) => set({ retained: e.target.value })}
                  />
                </label>
              </div>
              <label className="field cu-wide">
                <span>Notes</span>
                <textarea
                  disabled={locked}
                  value={form.notes}
                  placeholder="Anything that explains a variance."
                  onChange={(e) => set({ notes: e.target.value })}
                />
              </label>
            </section>

            {/* --------------------------------------------- the fortnight */}
            <section className="cu-block">
              <div className="label">
                The last {CASHUP_WINDOW_DAYS} nights
                {recent.length ? ` · ${recent.length} cashed up` : ""}
              </div>
              <div className="table cu-table">
                <div className="thead">
                  <span className="cu-c-date">Night</span>
                  <span className="cu-c-counted">Counted</span>
                  <span className="cu-c-var">Variance</span>
                  <span className="cu-c-status">Status</span>
                  <span className="cu-c-who">Counted by</span>
                </div>
                {recent.map((row) => {
                  const r = reconcileCashup(row);
                  return (
                    <button
                      className="trow"
                      key={row.serviceDate}
                      aria-current={row.serviceDate === date}
                      onClick={() => setDate(row.serviceDate)}
                    >
                      <span className="cu-c-date">{shortNight(row.serviceDate)}</span>
                      <span className="cu-c-counted">
                        {money(row.countedCashCents / 100)}
                      </span>
                      <span className={`cu-c-var ${BAND_CLASS[r.total.band]}`}>
                        {signedMoney(r.total.deltaCents)}
                      </span>
                      <span className="cu-c-status">
                        {row.status === "finalised" ? "Signed off" : "Draft"}
                      </span>
                      <span className="cu-c-who">{who(row.countedBy)}</span>
                    </button>
                  );
                })}
                {recent.length === 0 && !loading && (
                  <div className="pad label">
                    No cash-up on file for this venue yet. Tonight is the first.
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        {/* ------------------------------------------------- reconciliation */}
        <aside className="aside">
          <div className="row" style={{ padding: "16px 20px 0" }}>
            <div className="label">Reconciliation · {date}</div>
          </div>

          <div className="scroll" style={{ padding: "12px 20px 20px" }}>
            <div className="cu-headline">
              <span className="label">Over or short, all in</span>
              <span className={`v ${BAND_CLASS[reconciliation.total.band]}`}>
                {signedMoney(reconciliation.total.deltaCents)}
              </span>
              <span className="s">{BAND_WORD[reconciliation.total.band]}</span>
            </div>

            <div className="kv">
              <span className="k">Takings</span>
              <span className="v">{money(reconciliation.takingsCents / 100)}</span>
            </div>
            <div className="kv">
              <span className="k">Expected in drawer</span>
              <span className="v">{money(reconciliation.expectedCashCents / 100)}</span>
            </div>
            <div className="kv">
              <span className="k">Counted</span>
              <span className="v">{money(countedCents / 100)}</span>
            </div>
            <div className="kv">
              <span className="k">Cash variance</span>
              <span className={`v ${BAND_CLASS[reconciliation.cash.band]}`}>
                {signedMoney(reconciliation.cash.deltaCents)}
              </span>
            </div>
            <div className="kv">
              <span className="k">Card variance</span>
              <span className={`v ${BAND_CLASS[reconciliation.card.band]}`}>
                {signedMoney(reconciliation.card.deltaCents)}
              </span>
            </div>
            <div className="kv">
              <span className="k">Drawer after banking</span>
              <span className={`v ${reconciliation.drawerBalances ? "pos" : "neg"}`}>
                {reconciliation.drawerBalances
                  ? "✓ balances"
                  : `${signedMoney(reconciliation.drawerDeltaCents)} out`}
              </span>
            </div>

            <p className="cu-explain">
              {reconciliation.drawerBalances
                ? "Counted, banked and left behind agree."
                : "What was counted does not match what was banked plus the float retained. One of the three figures is wrong."}
            </p>

            {finalised && saved && (
              <p className="cu-explain">
                Signed off by {who(saved.finalisedBy)} on{" "}
                {new Date(saved.finalisedAt ?? "").toLocaleString("en-AU", { timeZone: venueZone() })}. Counted by{" "}
                {who(saved.countedBy)}.
              </p>
            )}
          </div>

          {!finalised && (
            <div className="foot">
              <button
                className="btn"
                disabled={busy || loading || !canRecord}
                onClick={() => void save()}
              >
                Save draft
              </button>
              {canFinalise ? (
                <button
                  className="btn primary"
                  disabled={busy || loading || !saved || dirty}
                  title={
                    !saved
                      ? "Save the count first."
                      : dirty
                        ? "Save the changes first."
                        : undefined
                  }
                  onClick={() => void finalise()}
                >
                  Finalise
                </button>
              ) : (
                <span className="label" style={{ alignSelf: "center" }}>
                  A manager signs the night off.
                </span>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function message(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "The cash-up could not be loaded";
}
